import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { Progress } from '@sorare/core/src/atoms/loader/Progress';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { range } from '@sorare/core/src/lib/arrays';
import { isType } from '@sorare/core/src/lib/gql';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { DailyActionTaskReward } from '../DailyActionTaskReward';
import type {
  ClaimDailyActionTaskMutation,
  ClaimDailyActionTaskMutationVariables,
  DailyActionTaskRewards_taskInterface,
} from './__generated__/index.graphql';

const Root = styled(Horizontal)`
  width: 100%;
  justify-content: flex-end;
  padding-bottom: var(--double-unit);
`;

const StyledProgress = styled(Progress)`
  flex: 1;
  @media ${tabletAndAbove} {
    flex: unset;
    min-width: 60px;
  }
`;

const Step = styled.div`
  position: relative;
  flex: 1;
`;
const Reward = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  margin: auto;
`;

const CLAIM_DAILY_ACTION_TASK_MUTATION = gql`
  mutation ClaimDailyActionTaskMutation($taskId: ID!) {
    claimTask(input: { taskId: $taskId }) {
      task {
        id
        aasmState
        ... on DailyActionTask {
          id
          liveScore
          thresholds {
            count
            current
          }
        }
      }
    }
  }
` as TypedDocumentNode<
  ClaimDailyActionTaskMutation,
  ClaimDailyActionTaskMutationVariables
>;
type Props = {
  task: DailyActionTaskRewards_taskInterface;
};

export const DailyActionTaskRewards = ({ task }: Props) => {
  const [claimTask, { loading: loadingClaimTask }] = useMutation(
    CLAIM_DAILY_ACTION_TASK_MUTATION,
    {
      showErrorsWithSnackNotification: true,
    }
  );
  if (!isType(task, 'DailyActionTask')) {
    return null;
  }
  const { thresholds, aasmState, liveScore } = task;

  const currentThreshold =
    thresholds.find(threshold => threshold.current)?.count || 0;

  const getRewardState = (index: number) => {
    if (index < currentThreshold - 1) {
      return 'claimed';
    }
    if (index === currentThreshold - 1) {
      if (loadingClaimTask) {
        return 'claiming';
      }
      if (aasmState === ManagerProgressionTaskState.COMPLETED) {
        return 'claimable';
      }
      if (aasmState === ManagerProgressionTaskState.CLAIMED) {
        return 'claimed';
      }
    }
    return 'locked';
  };

  const target = thresholds.at(-1)?.count;
  if (typeof target !== 'number' || target < 0) {
    return null;
  }

  return (
    <Root gap={0.5}>
      {range(target).map(index => {
        const rewardConfig = thresholds
          .find(threshold => threshold.count - 1 === index)
          ?.rewardConfigs.at(0);
        return (
          <Step key={index}>
            <StyledProgress
              value={index <= liveScore - 1 ? 1 : 0}
              max={1}
              color="linear-gradient(to right, #FFB800 0%, #F0CE1D 37.72%, #FCFF63 100%)"
              skipStartAnimation
            />
            {!!rewardConfig && (
              <Reward>
                <DailyActionTaskReward
                  state={getRewardState(index)}
                  anyRewardConfig={rewardConfig}
                  onClaim={async () => {
                    await claimTask({
                      variables: {
                        taskId: task.id,
                      },
                    });
                  }}
                />
              </Reward>
            )}
          </Step>
        );
      })}
    </Root>
  );
};

DailyActionTaskRewards.fragments = {
  taskInterface: gql`
    fragment DailyActionTaskRewards_taskInterface on TaskInterface {
      id
      aasmState
      ... on DailyActionTask {
        id
        liveScore
        thresholds {
          count
          current
          rewardConfigs {
            id
            ...DailyActionTaskReward_anyRewardConfig
          }
        }
      }
    }
    ${DailyActionTaskReward.fragments.anyRewardConfig}
  ` as TypedDocumentNode<DailyActionTaskRewards_taskInterface>,
};
